import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';

import {
  IconComponent,
  ObserveIntersectingDirective,
  MessageService,
  ProgressComponent
} from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';
import { toBlob, toPng } from 'html-to-image';
import { Share } from '@capacitor/share';
import { Directory, Filesystem } from '@capacitor/filesystem';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';
import { PaymentService } from '@/main/services/payment.service';
import { Payment } from '@/main/models/payment';
import { CreditService } from '@/main/services/credit.service';
import { Credit } from '@/main/models/credit';
import { ApiService } from '@/core/api.service';
import { environment } from '@/environments/environment';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';

/**
 * @description Componente para visualizar y gestionar detalles de un crédito
 * @class CreditComponent
 * @implements {OnInit, OnDestroy}
 */
@Component({
  selector: 'app-credit',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatMenuModule,
    MatDialogModule,
    MatTabsModule,
    IconComponent,
    MatTooltipModule,
    ObserveIntersectingDirective,
    ProgressComponent,
    IsGrantedDirective
  ],
  templateUrl: './credit.component.html',
  styleUrl: './credit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditComponent implements OnInit, OnDestroy {
  // Dependency injection
  apiService = inject(ApiService);
  appService = inject(AppService);
  sessionService = inject(SessionService);
  creditService = inject(CreditService);
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef);
  messageService = inject(MessageService);
  paymentService = inject(PaymentService);
  private subscriptions = new Subscription();
  private paymentElement =
    viewChild<ElementRef<HTMLDivElement>>('paymentElement');

  // Events
  afterSubmit = output<Operation>();

  // Properties
  credit = signal<Credit | undefined>(undefined);
  currencyId = signal<string>('');
  payments = signal<Payment[]>([]);
  paymentTable = signal<any[]>([]);
  today = new Date();
  pagePaymentTableLoading = signal<boolean>(false);
  pagePaymentTableLimit = environment.pageLimit;
  pagePaymentTableNumber = 1;
  pagePaymentTableCompleted = signal<boolean>(false);
  pagePaymentsLoading = signal<boolean>(false);
  pagePaymentsLimit = environment.pageLimit;
  pagePaymentsNumber = 1;
  pagePaymentsCompleted = signal<boolean>(false);
  paymentsLoaded = signal<boolean>(false);
  selectedPayment = signal<Payment | undefined>(undefined);
  selectedTab = signal<number>(0);
  public isBeneficiary = signal<boolean>(this.sessionService.params()['profileId'] === PROFILE_IDS.BasicBeneficiary);

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  async ngOnInit(): Promise<void> {
    const countries = this.sessionService
      .catalogs()
      .filter(c => c.catalogId === 'CPAMD');
    this.currencyId.set(
      countries?.find(c => c.id === this.data.credit.countryId)?.value || ''
    );
    this.credit.set(this.data.credit);
    if (this.data.tab === 'payments') {
      this.selectedTab.set(2);
    }
    this.paymentTable.set(await this.getPaymentTable(1));
    this.payments.set(await this.getPayments(1));
    this.paymentsLoaded.set(true);
    this.subscriptions.add(
      this.creditService.change.subscribe((operation: Operation) =>
        this.refresh('credit', operation)
      )
    );
    this.subscriptions.add(
      this.paymentService.change.subscribe((operation: Operation) => {
        this.refresh('payment', operation);
        this.refresh('credit', operation);
      })
    );
  }

  /**
   * @description Limpia las suscripciones al destruir el componente
   * @implements OnDestroy
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * @description Obtiene las acciones contextuales disponibles para un crédito
   * @param {Credit} credit - Crédito objetivo
   * @returns {any[]} Array de acciones disponibles
   */
  getPaymentContextActions(payment: Payment): any[] {
    let actions: any[];
    const credit = this.credit();
    if (!(credit?.operationNumber || credit?.operation?.number)) {
      actions = [];
    } else {
      const statusId = credit.status?.id ?? credit.operation?.status?.id;
      actions = [
        {
          id: 'actions',
          type: 'group',
          children: [
            {
              id: 'reverse',
              label: $localize`Reverse`,
              visible: statusId !== 'ANU' && !this.isBeneficiary(),
              click: async () => {
                const operation = await this.paymentService.reverse(credit, payment);
                if (operation) {
                  this.refresh('payment', operation);
                }
              }
            },
            {
              id: 'download',
              label: $localize`Download`,
              visible: !this.appService.isMobile,
              click: () => {
                this.sharePayment(payment, true);
              }
            },
            {
              id: 'share',
              label: $localize`Share`,
              visible: Capacitor.isNativePlatform(),
              click: () => {
                this.sharePayment(payment);
              }
            }
          ].filter(a => a.visible)
        }
      ].filter(g => g.children.length > 0);
    }
    return actions;
  }

  /**
   * @description Obtiene los pagos de un crédito
   * @param {number} pageNumber - Número de página a obtener
   * @returns {Promise<Payment[]>} Lista de pagos
   */
  async getPayments(pageNumber: number): Promise<Payment[]> {
    this.pagePaymentsLoading.set(true);
    const response: any = await this.apiService.call({
      microServiceName: 'WSMPCreditManagement',
      resourcePath: `/payment/get-payments/{operationNumber}/${this.pagePaymentsLimit}/${pageNumber}`,
      method: 'GET',
      useToken: true,
      pathParams: {
        operationNumber: {
          value: this.isBeneficiary() ? this.credit()?.operation.number : this.credit()?.operationNumber,
          encrypted: true
        }
      },
      responseEncrypted: true
    });
    const payments = response.data;
    this.pagePaymentsLoading.set(false);
    this.pagePaymentsCompleted.set(payments.length < this.pagePaymentsLimit);
    this.pagePaymentsNumber = pageNumber;
    return payments as Payment[];
  }

  /**
   * @description Obtiene la tabla de amortización
   * @param {number} pageNumber - Número de página a obtener
   * @returns {Promise<any[]>} Tabla de amortización
   */
  async getPaymentTable(pageNumber: number): Promise<any[]> {
    this.pagePaymentTableLoading.set(true);
    const response: any = await this.apiService.call({
      microServiceName: 'WSMPCreditManagement',
      resourcePath: `/offercredit/get-amort-table/{operationNumber}/${this.pagePaymentTableLimit}/${pageNumber}`,
      method: 'GET',
      useToken: true,
      pathParams: {
        operationNumber: {
          value: this.isBeneficiary() ? this.credit()?.operation.number : this.credit()?.operationNumber,
          encrypted: true
        }
      },
      responseEncrypted: true
    });
    const paymentTable = response.data.map((p: any) => {
      p.hash = this.appService.getHash(p);
      return p;
    });
    this.pagePaymentTableLoading.set(false);
    this.pagePaymentTableCompleted.set(
      paymentTable.length < this.pagePaymentTableLimit
    );
    this.pagePaymentTableNumber = pageNumber;
    return paymentTable as Payment[];
  }

  /**
   * @description Pasa a la siguiente página
   * @param {any} event - Evento
   * @returns {Promise<void>}
   */
  async nextPage(event: any): Promise<void> {
    if (
      event &&
      !this.pagePaymentTableCompleted() &&
      !this.pagePaymentTableLoading() &&
      (this.paymentTable().length || 0) > 0
    ) {
      const nextPaymentTable = await this.getPaymentTable(
        this.pagePaymentTableNumber + 1
      );
      this.paymentTable.set(this.paymentTable().concat(nextPaymentTable || []));
    }
  }

  /**
   * @description Actualiza los datos después de una operación
   * @param {'credit' | 'payment'} entityType - Tipo de entidad actualizada
   * @param {Operation} operation - Detalles de la operación
   * @returns {Promise<void>}
   */
  async refresh(
    entityType: 'credit' | 'payment',
    operation: Operation
  ): Promise<void> {
    // Si el crédito es anulado o se hace una precancelación se cierra la ventana
    if (
      (entityType === 'credit' && operation.entity.status?.id === 'CAN') ||
      (entityType === 'payment' && operation.entity.ticketType === 'PRE')
    ) {
      this.dialogRef.close();
    }

    if (!this.isBeneficiary()) {
      const lenderId = this.sessionService.settings()?.lenderId;
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/credit/get-credit-data/{lenderId}/{operationNumber}`,
        method: 'GET',
        useToken: true,
        pathParams: {
          lenderId: {
            value: lenderId,
            encrypted: true
          },
          operationNumber: {
            value: this.isBeneficiary() ? this.credit()?.operation.number : this.credit()?.operationNumber,
            encrypted: true
          }
        },
        responseEncrypted: true
      });
      this.credit.set(response.data);
    } else {
    this.credit.update((credit: Credit | undefined) => {
      if (credit && entityType === 'payment') {
        credit.amountRecoveredToDate =
          operation.entity.amountRecoveredToDate ?? credit.amountRecoveredToDate;
        credit.interestEarnedToDate =
          operation.entity.interestEarnedToDate ?? credit.interestEarnedToDate;
        credit.expirationDate = operation.entity.dueDateCredit ?? credit.expirationDate;
        credit.amountOverdueToDate = operation.entity.amountOverdueToDate ?? credit.amountOverdueToDate;
      }
      if (credit?.beneficiary) {
        credit.beneficiary = {
          ...credit.beneficiary,
          ...operation.entity.beneficiary
        };
        // @todo: las propiedades de notificaciones deberian estar dentro de beneficiary en todos los lugares
        credit = { ...credit, ...operation.entity.beneficiary };
      }
      if (credit?.group) {
        credit.group = {
          ...credit.group,
          ...operation.entity.group
        };
      }
      return credit;
    });
  }
    this.paymentTable.set(await this.getPaymentTable(1));
    this.payments.set(await this.getPayments(1));
  }

  async sharePayment(payment: Payment, download?: boolean): Promise<void> {
    const fileName = $localize`receipt` + `-${payment.ticketNumber}.png`;
    if (Capacitor.isNativePlatform()) {
      this.nativeSharePayment(fileName, payment);
      return;
    }
    const timerStart = performance.now();
    const paymentElement = this.paymentElement()?.nativeElement;
    if (paymentElement) {
      this.selectedPayment.set(payment);
      setTimeout(async () => {
        const isFirefox =
          navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        const blob = await toBlob(paymentElement, {
          skipFonts: isFirefox
        });
        // console.log(
        //   'File generated in:',
        //   (performance.now() - timerStart).toFixed(2),
        //   'ms'
        // );
        if (blob) {
          if (!download) {
            const file = new File([blob], fileName, {
              type: 'image/png'
            });
            await navigator.share({
              files: [file],
              title: '',
              text: ''
            });
          } else {
            // Crear un enlace temporal y descargar el archivo
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName; // Cambia el nombre del archivo según tus necesidades.
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Liberar el objeto URL
            URL.revokeObjectURL(url);
          }
        }
      });
    }
  }
  async nativeSharePayment(fileName: string, payment: Payment): Promise<void> {
    const paymentElement = this.paymentElement()?.nativeElement;
    if (paymentElement) {
      this.selectedPayment.set(payment);
      setTimeout(async () => {
        const data = await toPng(paymentElement);
        const path = fileName;
        await Filesystem.writeFile({
          path,
          data,
          directory: Directory.Cache
        });
        const uriResult = await Filesystem.getUri({
          directory: Directory.Cache,
          path
        });
        Share.share({
          files: [uriResult.uri],
          title: ''
        });
      });
    }
  }
}
